function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

import get from 'lodash/get';
import isEqual from 'lodash/isEqual';
import set from 'lodash/set';
import { ALL_OF_KEY, ANY_OF_KEY, ADDITIONAL_PROPERTIES_KEY, DEPENDENCIES_KEY, ITEMS_KEY, NAME_KEY, ONE_OF_KEY, PROPERTIES_KEY, REF_KEY, RJSF_ADDITIONAL_PROPERTIES_FLAG } from "../constants";
import getDiscriminatorFieldFromSchema from "../getDiscriminatorFieldFromSchema";
import getClosestMatchingOption from "./getClosestMatchingOption";
import retrieveSchema from "./retrieveSchema";
/** An internal helper that generates an `PathSchema` object for the `schema`, recursively with protection against
 * infinite recursion
 *
 * @param validator - An implementation of the `ValidatorType` interface that will be used when necessary
 * @param schema - The schema for which the `PathSchema` is desired
 * @param [name=''] - The base name for the schema
 * @param [rootSchema] - The root schema, used to primarily to look up `$ref`s
 * @param [formData] - The current formData, if any, to assist retrieving a schema
 * @param [_recurseList=[]] - The list of retrieved schemas currently being recursed, used to prevent infinite recursion
 * @returns - The `PathSchema` object for the `schema`
 */

function toPathSchemaInternal(validator, schema, name, rootSchema, formData) {
  var _recurseList = arguments.length > 5 && arguments[5] !== undefined ? arguments[5] : [];

  if (REF_KEY in schema || DEPENDENCIES_KEY in schema || ALL_OF_KEY in schema) {
    var _schema = retrieveSchema(validator, schema, rootSchema, formData);

    var sameSchemaIndex = _recurseList.findIndex(function (item) {
      return isEqual(item, _schema);
    });

    if (sameSchemaIndex === -1) {
      return toPathSchemaInternal(validator, _schema, name, rootSchema, formData, _recurseList.concat(_schema));
    }
  }

  var pathSchema = _defineProperty({}, NAME_KEY, name.replace(/^\./, ''));

  if (ONE_OF_KEY in schema || ANY_OF_KEY in schema) {
    var xxxOf = ONE_OF_KEY in schema ? schema.oneOf : schema.anyOf;
    var discriminator = getDiscriminatorFieldFromSchema(schema);
    var index = getClosestMatchingOption(validator, rootSchema, formData, xxxOf, 0, discriminator);
    var _schema2 = xxxOf[index];
    pathSchema = _objectSpread(_objectSpread({}, pathSchema), toPathSchemaInternal(validator, _schema2, name, rootSchema, formData, _recurseList));
  }

  if (ADDITIONAL_PROPERTIES_KEY in schema && schema[ADDITIONAL_PROPERTIES_KEY] !== false) {
    set(pathSchema, RJSF_ADDITIONAL_PROPERTIES_FLAG, true);
  }

  if (ITEMS_KEY in schema && Array.isArray(formData)) {
    var schemaItems = schema.items,
        schemaAdditionalItems = schema.additionalItems;

    if (Array.isArray(schemaItems)) {
      formData.forEach(function (element, i) {
        if (schemaItems[i]) {
          pathSchema[i] = toPathSchemaInternal(validator, schemaItems[i], "".concat(name, ".").concat(i), rootSchema, element, _recurseList);
        } else if (schemaAdditionalItems) {
          pathSchema[i] = toPathSchemaInternal(validator, schemaAdditionalItems, "".concat(name, ".").concat(i), rootSchema, element, _recurseList);
        } else {
          console.warn("Unable to generate path schema for \"".concat(name, ".").concat(i, "\". No schema defined for it"));
        }
      });
    } else {
      formData.forEach(function (element, i) {
        pathSchema[i] = toPathSchemaInternal(validator, schemaItems, "".concat(name, ".").concat(i), rootSchema, element, _recurseList);
      });
    }
  } else if (PROPERTIES_KEY in schema) {
    for (var property in schema.properties) {
      var field = get(schema, [PROPERTIES_KEY, property]);
      pathSchema[property] = toPathSchemaInternal(validator, field, "".concat(name, ".").concat(property), rootSchema, // It's possible that formData is not an object -- this can happen if an
      // array item has just been added, but not populated with data yet
      get(formData, [property]), _recurseList);
    }
  }

  return pathSchema;
}
/** Generates an `PathSchema` object for the `schema`, recursively
 *
 * @param validator - An implementation of the `ValidatorType` interface that will be used when necessary
 * @param schema - The schema for which the `PathSchema` is desired
 * @param [name=''] - The base name for the schema
 * @param [rootSchema] - The root schema, used to primarily to look up `$ref`s
 * @param [formData] - The current formData, if any, to assist retrieving a schema
 * @returns - The `PathSchema` object for the `schema`
 */


export default function toPathSchema(validator, schema) {
  var name = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : '';
  var rootSchema = arguments.length > 3 ? arguments[3] : undefined;
  var formData = arguments.length > 4 ? arguments[4] : undefined;
  return toPathSchemaInternal(validator, schema, name, rootSchema, formData);
}